/**
 * Bulk action buttons when drafts are selected.
 */

import type { FC } from 'react';
import { UploadCloud, CopyPlus, Edit3, Layers, Send, Trash2, X } from 'lucide-react';
import { ClipboardPaste } from 'lucide-react';

import { Button } from '@/components/ui';

export interface DraftsSelectionActionButtonsProps {
  selectedCount: number;
  isBusy: boolean;
  bulkSendRunning: boolean;
  onDelete: () => void;
  onBulkEdit: () => void;
  onDuplicate?: () => void;
  onSaveAsPreset?: () => void;
  onApply?: () => void;
  hasCopiedValues?: boolean;
  onBulkSend: () => void;
  onCreateSmswiserDraft?: () => void;
  /** Count of selected drafts not yet staged on SMSWiser (the create-draft label). */
  smswiserDraftCount?: number;
  onClearSelection: () => void;
}

export const DraftsSelectionActionButtons: FC<DraftsSelectionActionButtonsProps> = ({
  selectedCount,
  isBusy,
  bulkSendRunning,
  onDelete,
  onBulkEdit,
  onDuplicate,
  onSaveAsPreset,
  onApply,
  hasCopiedValues,
  onBulkSend,
  onCreateSmswiserDraft,
  smswiserDraftCount,
  onClearSelection,
}) => (
  <div className="flex items-center gap-2">
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
      disabled={isBusy || bulkSendRunning}
      onClick={onDelete}
      aria-label="Delete selected drafts"
    >
      <Trash2 className="h-4 w-4" />
      Delete ({selectedCount})
    </Button>
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-2"
      disabled={isBusy || bulkSendRunning}
      onClick={onBulkEdit}
    >
      <Edit3 className="h-4 w-4" />
      Bulk edit ({selectedCount})
    </Button>
    {onDuplicate && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || bulkSendRunning}
        onClick={onDuplicate}
        aria-label="Duplicate selected drafts"
      >
        <CopyPlus className="h-4 w-4" />
        Duplicate ({selectedCount})
      </Button>
    )}
    {onSaveAsPreset && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || bulkSendRunning}
        onClick={onSaveAsPreset}
        aria-label="Save as preset"
      >
        <Layers className="h-4 w-4" />
        Save as preset ({selectedCount})
      </Button>
    )}
    {onApply && hasCopiedValues && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={isBusy || bulkSendRunning}
        onClick={onApply}
        aria-label="Apply copied campaign, gateway, senders"
        title="Apply copied values to selected drafts"
      >
        <ClipboardPaste className="h-4 w-4" />
        Apply ({selectedCount})
      </Button>
    )}
    {onCreateSmswiserDraft && (
      <Button
        type="button"
        variant="orange"
        size="sm"
        className="gap-2"
        disabled={isBusy || bulkSendRunning}
        onClick={onCreateSmswiserDraft}
        aria-label="Create SMSWiser draft"
        title="Stage the selected drafts on SMSWiser (create real drafts)"
      >
        <UploadCloud className="h-4 w-4" />
        Create draft ({smswiserDraftCount ?? selectedCount})
      </Button>
    )}
    <Button
      type="button"
      size="sm"
      className="gap-2"
      disabled={isBusy || bulkSendRunning}
      onClick={onBulkSend}
    >
      <Send className="h-4 w-4" />
      Send selected
    </Button>
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-9 w-9 p-0"
      onClick={onClearSelection}
      disabled={isBusy || bulkSendRunning}
      aria-label="Clear selection"
      title="Clear"
    >
      <X className="h-4 w-4" />
    </Button>
  </div>
);
